/**
 * mobileStability.js — Централизованная стабилизация мобильного UI
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v6.1.0 — QWEN 3.8 PATCHED]:
 *  ✅ FIXED: Memory leak в tutorial dialog (ESC close)
 *  ✅ FIXED: Battery watcher cleanup
 *  ✅ FIXED: Race condition с AbortController
 *  ✅ NEW: Throttling для visibility recovery
 *  ✅ NEW: Fallback для env() safe-area
 *  ✅ NEW: Network-aware quality adjustment
 *  ✅ IMPROVED: Performance score (3s test вместо 1s)
 *  ✅ IMPROVED: Unified RenderingEngine access
 */

import { log } from '../utils/logger.js';
import { showToast } from '../utils/helpers.js';

// ══════════════════════════════════════════════════════════════
//  GLOBAL STATE
// ══════════════════════════════════════════════════════════════

let _ac = new AbortController();
let _tutorialShown = false;
let _qualityMode = 'auto'; // 'high'|'balanced'|'low'|'auto'
let _batteryWatcher = null;
let _visibilityThrottle = null;
let _networkWatcher = null;
let _resizeObserver = null;   // [10.0.1] нужен в destroy — AbortController его не гасит
let _resizeTimeout = null;
let _tutorialTimer = null;   // [10.0.1] отложенный показ туториала — гасится в destroy

// Более надежная проверка на тач-устройства (включая iPad Pro, маскирующиеся под десктоп)
const IS_TOUCH = (() => {
  try {
    return navigator.maxTouchPoints > 0 || 
           window.matchMedia('(pointer: coarse)').matches ||
           'ontouchstart' in window;
  } catch {
    return false;
  }
})();

// ══════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════

/**
 * Унифицированный доступ к RenderingEngine
 * @returns {Object|null}
 */
function _getRenderingEngine() {
  try {
    return window.MikhmelViewer?.getRenderingEngine?.() ||
           window.cornerstone?.getRenderingEngine?.('mikhmelRenderingEngine') ||
           null;
  } catch (e) {
    log.debug('[Mobile] getRenderingEngine failed:', e.message);
    return null;
  }
}

/**
 * Безопасный вызов resize для всех viewport'ов
 * @param {boolean} force - Принудительный resize
 */
function _safeResizeViewports(force = false) {
  const re = _getRenderingEngine();
  if (!re) return;
  
  try {
    re.resize(force, force);
  } catch (e) {
    log.warn('[Mobile] Resize error, перерисовываю окна:', e.message);
    setTimeout(() => {
      (re.getViewports?.() || []).forEach(vp => {
        try { vp.render?.(); } catch (_) { /* Silent fail */ }
      });
    }, 80);
  }
}

// ══════════════════════════════════════════════════════════════
//  1. TOUCH-ACTION ENFORCEMENT (CSS-ONLY)
// ══════════════════════════════════════════════════════════════

/**
 * Принудительно ставит touch-action:none через CSS.
 * Браузер сам применит его ко всем будущим canvas с нулевой задержкой.
 */
export function enforceTouchAction() {
  if (!IS_TOUCH) return;
  
  if (document.getElementById('mikhmel-touch-enforce')) return;

  const style = document.createElement('style');
  style.id = 'mikhmel-touch-enforce';
  style.textContent = `
    #viewport-container, 
    #viewport-container * {
      touch-action: none !important;
      user-select: none !important;
      -webkit-user-select: none !important;
      -webkit-touch-callout: none !important;
    }
  `;
  document.head.appendChild(style);
  log.debug('[Mobile] CSS Touch enforcement applied');
}

// ══════════════════════════════════════════════════════════════
//  2. PREVENT NATIVE GESTURES ON VIEWPORT
// ══════════════════════════════════════════════════════════════

function _preventNativeGestures() {
  if (!IS_TOUCH) return;

  const container = document.getElementById('viewport-container');
  if (!container) return;

  // Prevent double-tap native zoom (iOS Safari edge case)
  let lastTap = 0;
  container.addEventListener('touchend', (e) => {
    const now = Date.now();
    if (now - lastTap < 300) {
      e.preventDefault();
    }
    lastTap = now;
  }, { passive: false, signal: _ac.signal });

  // Prevent pinch-zoom on viewport level
  container.addEventListener('touchmove', (e) => {
    if (e.touches.length >= 2) {
      e.preventDefault();
    }
  }, { passive: false, signal: _ac.signal });

  // Prevent context menu (long press for iOS/Android)
  container.addEventListener('contextmenu', e => e.preventDefault(), { signal: _ac.signal });
  
  // [10.0.1] Pull-to-refresh глушится через CSS overscroll-behavior, а не
  // через preventDefault на touchstart.
  //
  // Прежний вариант звал preventDefault на ЛЮБОМ одиночном касании в верхних
  // 50 px контейнера, не разбирая цель. Во-первых, preventDefault на touchstart
  // подавляет синтезируемый click — кнопки зума и оверлеи, которые лежат сверху
  // (.vp-overlay-tr стоит на top:6px), переставали нажиматься. Во-вторых, все
  // остальные тач-обработчики проекта начинаются с проверки
  // `e.target.closest('.vp-zoom-btn, ..., button')`, а этот её не делал —
  // расхождение в одном и том же файле рано или поздно выстрелит.
  //
  // overscroll-behavior-y: contain — штатный механизм ровно для этой задачи:
  // отменяет только «протягивание» за границу прокрутки, ничего не ломая.
  if (!document.getElementById('mikhmel-overscroll-guard')) {
    const s = document.createElement('style');
    s.id = 'mikhmel-overscroll-guard';
    s.textContent = 'html, body { overscroll-behavior-y: contain; }\n'
      + '#viewport-container { overscroll-behavior: none; }';
    document.head.appendChild(s);
  }
}

// ══════════════════════════════════════════════════════════════
//  3. RESIZE / ORIENTATION OBSERVER
// ══════════════════════════════════════════════════════════════

function _handleOrientationChange() {
  if (!IS_TOUCH) return;

  const container = document.getElementById('viewport-container');
  if (!container) return;

  const debounceResize = () => {
    clearTimeout(_resizeTimeout);
    _resizeTimeout = setTimeout(() => _safeResizeViewports(true), 150);
  };

  // 1. ResizeObserver: Срабатывает ТОЛЬКО когда меняются размеры контейнера
  // [10.0.1] Наблюдатель и его таймер вынесены в модульный scope. Раньше это
  // были локальные переменные, до которых destroyMobileStability не дотягивался:
  // AbortController гасит слушатели, но ResizeObserver ему не подчиняется, и
  // после destroy наблюдатель продолжал дёргать resize уже мёртвых вьюпортов.
  _resizeObserver?.disconnect();
  _resizeObserver = new ResizeObserver(debounceResize);
  _resizeObserver.observe(container);

  // 2. VisualViewport: Реагирует на открытие экранной клавиатуры
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', debounceResize, { 
      passive: true, 
      signal: _ac.signal 
    });
  }

  // 3. Fallback на поворот экрана
  window.addEventListener('orientationchange', () => {
    setTimeout(() => _safeResizeViewports(true), 300);
  }, { passive: true, signal: _ac.signal });
}

// ══════════════════════════════════════════════════════════════
//  4. SAFE AREA INSETS (iPhone notch)
// ══════════════════════════════════════════════════════════════

function _applySafeAreaInsets() {
  if (!IS_TOUCH) return;

  const meta = document.querySelector('meta[name="viewport"]');
  if (meta && !meta.content.includes('viewport-fit=cover')) {
    meta.content += ', viewport-fit=cover';
  }

  // [FIX] Fallback для старых браузеров без поддержки env()
  const supportsEnv = CSS.supports?.('padding', 'env(safe-area-inset-top)') ?? false;
  
  document.head.insertAdjacentHTML('beforeend', `
    <style id="mikhmel-safe-area">
      #mobile-nav { 
        padding-bottom: ${supportsEnv ? 'env(safe-area-inset-bottom, 0)' : '20px'}; 
      }
      #mobile-topbar { 
        padding-top: ${supportsEnv ? 'env(safe-area-inset-top, 0)' : '20px'}; 
      }
      .mobile-drawer { 
        padding-bottom: ${supportsEnv ? 'env(safe-area-inset-bottom, 0)' : '20px'}; 
      }
    </style>
  `);
}

// ══════════════════════════════════════════════════════════════
//  5. GESTURE TUTORIAL (HTML <dialog>)
// ══════════════════════════════════════════════════════════════

function _showGestureTutorial() {
  if (!IS_TOUCH || _tutorialShown) return;

  const TUTORIAL_KEY = 'mikhmel_gesture_tutorial_v2';
  try {
    if (localStorage.getItem(TUTORIAL_KEY)) return;
  } catch (e) {
    // localStorage may be unavailable (private mode)
  }

  _tutorialShown = true;

  const dialogHtml = `
    <dialog id="gesture-tutorial-dialog" style="
      padding: 24px; border: none; border-radius: 16px; background: #1e1e1e; color: #fff;
      max-width: 340px; text-align: center; font-family: inherit;
      box-shadow: 0 10px 40px rgba(0,0,0,0.5);
    ">
      <div style="font-size: 22px; margin-bottom: 24px; font-weight: bold;">👆 Управление жестами</div>
      
      <div style="display: grid; grid-template-columns: 50px 1fr; gap: 16px; text-align: left; margin-bottom: 28px;">
        <div style="font-size: 28px; text-align: center;">☝️</div>
        <div>
          <div style="font-weight: 600; font-size: 15px;">1 палец</div>
          <div style="font-size: 13px; opacity: 0.7; margin-top:2px;">Текущий инструмент (W/L, линейка)</div>
        </div>

        <div style="font-size: 28px; text-align: center;">✌️</div>
        <div>
          <div style="font-weight: 600; font-size: 15px;">2 пальца</div>
          <div style="font-size: 13px; opacity: 0.7; margin-top:2px;">Масштаб (щипок) и перемещение</div>
        </div>

        <div style="font-size: 28px; text-align: center;">👆👆</div>
        <div>
          <div style="font-weight: 600; font-size: 15px;">Двойной тап</div>
          <div style="font-size: 13px; opacity: 0.7; margin-top:2px;">Окно на весь экран (и обратно)</div>
        </div>

        <div style="font-size: 28px; text-align: center;">↕️</div>
        <div>
          <div style="font-weight: 600; font-size: 15px;">Свайп по краю</div>
          <div style="font-size: 13px; opacity: 0.7; margin-top:2px;">Смена серии / проекции</div>
        </div>
      </div>

      <button id="tutorial-close" style="
        padding: 14px; border: none; border-radius: 12px;
        background: var(--accent, #3b82f6); color: #fff; font-size: 16px;
        cursor: pointer; font-weight: 600; width: 100%; transition: opacity 0.2s;
      ">Начать работу</button>

      <label style="display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 16px; font-size: 13px; opacity: 0.7; cursor: pointer;">
        <input type="checkbox" id="tutorial-noshow" checked style="width: 16px; height: 16px; accent-color: var(--accent, #3b82f6);">
        Больше не показывать
      </label>
    </dialog>
  `;

  // [10.0.1] Стиль подложки вынесен из разметки диалога и ставится один раз
  // по идентификатору. Раньше <style> вставлялся вместе с <dialog>, а при
  // закрытии удалялся только сам диалог — каждый показ туториала оставлял в
  // <head> ещё один осиротевший <style>. Одна из тех утечек, которые в шапке
  // файла помечены как исправленные.
  if (!document.getElementById('mikhmel-tutorial-backdrop')) {
    const backdropStyle = document.createElement('style');
    backdropStyle.id = 'mikhmel-tutorial-backdrop';
    backdropStyle.textContent =
      'dialog::backdrop { background: rgba(0,0,0,.7); backdrop-filter: blur(4px); }';
    document.head.appendChild(backdropStyle);
  }

  document.body.insertAdjacentHTML('beforeend', dialogHtml);
  const dialog = document.getElementById('gesture-tutorial-dialog');
  
  dialog.showModal();

  const closeHandler = () => {
    if (dialog.querySelector('#tutorial-noshow')?.checked) {
      try { localStorage.setItem(TUTORIAL_KEY, '1'); } catch { /* ignore */ }
    }
    dialog.close();
  };

  // [10.0.1] Слушатель кнопки не привязан к _ac: после destroyMobileStability
  // там null, и `_ac.signal` уронил бы showTutorial() с TypeError. Диалог и так
  // снимается целиком в обработчике 'close' ниже, отдельная отмена ему не нужна.
  dialog.querySelector('#tutorial-close').addEventListener('click', closeHandler);

  // [FIX] Cleanup при закрытии через ESC или backdrop click
  dialog.addEventListener('close', () => {
    dialog.remove();
  }, { once: true });
}

// ══════════════════════════════════════════════════════════════
//  6. VIEWPORT VISIBILITY RECOVERY
// ══════════════════════════════════════════════════════════════

function _setupVisibilityRecovery() {
  if (!IS_TOUCH) return;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // [FIX] Throttling для предотвращения spam вызовов
      if (_visibilityThrottle) return;
      
      _visibilityThrottle = setTimeout(() => {
        _visibilityThrottle = null;
      }, 1000); // Максимум 1 вызов в секунду
      
      requestAnimationFrame(() => {
        setTimeout(() => _safeResizeViewports(true), 200);
      });
    }
  }, { signal: _ac.signal });
}

// ══════════════════════════════════════════════════════════════
//  7. BATTERY-AWARE QUALITY
// ══════════════════════════════════════════════════════════════

/**
 * Battery-aware quality: reduce rendering quality when battery is low
 * Auto-enables lower quality below 20%, restores above 50%
 */
export async function enableBatteryAwareQuality() {
  if (!navigator.getBattery) {
    log.debug('[Mobile] Battery API not available');
    return;
  }
  
  try {
    const battery = await navigator.getBattery();
    
    const check = () => {
      if (!battery.charging && battery.level < 0.2) {
        if (_qualityMode !== 'low') setQualityMode('low');
      } else if (battery.charging || battery.level > 0.5) {
        if (_qualityMode === 'low') setQualityMode('auto');
      }
    };
    
    battery.addEventListener('levelchange', check, { signal: _ac.signal });
    battery.addEventListener('chargingchange', check, { signal: _ac.signal });
    _batteryWatcher = battery;
    check();
    log.debug('[Mobile] Battery-aware quality enabled');
  } catch (e) {
    log.warn('[Mobile] Battery API error:', e.message);
  }
}

/**
 * Get battery info
 * @returns {Promise<{level:number, charging:boolean, dischargingTimeSec:number}|null>}
 */
export async function getBatteryInfo() {
  if (!navigator.getBattery) return null;
  
  try {
    const b = await navigator.getBattery();
    return {
      level: +(b.level * 100).toFixed(0),
      charging: b.charging,
      dischargingTimeSec: b.dischargingTime
    };
  } catch {
    return null;
  }
}

// ══════════════════════════════════════════════════════════════
//  8. NETWORK-AWARE QUALITY
// ══════════════════════════════════════════════════════════════

/**
 * Network-aware quality: reduce quality on slow connections
 */
export function enableNetworkAwareQuality() {
  if (!navigator.connection) {
    log.debug('[Mobile] Network Information API not available');
    return;
  }
  
  const check = () => {
    const conn = navigator.connection;
    const effectiveType = conn.effectiveType;
    
    if (effectiveType === '2g' || effectiveType === 'slow-2g') {
      if (_qualityMode !== 'low') setQualityMode('low');
    } else if (effectiveType === '3g') {
      if (_qualityMode === 'high') setQualityMode('balanced');
    } else if (effectiveType === '4g' && _qualityMode === 'low') {
      setQualityMode('auto');
    }
  };
  
  navigator.connection.addEventListener('change', check, { signal: _ac.signal });
  _networkWatcher = navigator.connection;
  check();
  log.debug('[Mobile] Network-aware quality enabled');
}

// ══════════════════════════════════════════════════════════════
//  9. QUALITY MODE CONTROL
// ══════════════════════════════════════════════════════════════

/**
 * Set rendering quality mode
 * @param {'high'|'balanced'|'low'|'auto'} mode
 */
export function setQualityMode(mode) {
  _qualityMode = mode;
  
  const re = _getRenderingEngine();
  if (!re) return;
  
  try {
    const scales = { high: 1.0, balanced: 0.75, low: 0.5, auto: 1.0 };
    const scale = scales[mode] || 1.0;
    
    for (const vp of re.getViewports()) {
      try {
        if (vp.setRenderedResolution) {
          vp.setRenderedResolution(scale);
        } else if (vp.element) {
          const canvas = vp.element.querySelector('canvas');
          if (canvas) {
            canvas.style.imageRendering = scale < 0.8 ? 'pixelated' : '';
          }
        }
      } catch (e) {
        log.debug('[Mobile] setQualityMode viewport error:', e.message);
      }
    }
    
    re.resize(true, true);
    showToast(`📱 Качество: ${mode} (${Math.round(scale * 100)}%)`, 'info', 1500);
  } catch (e) {
    log.warn('[Mobile] Quality mode error:', e.message);
  }
}

/**
 * Get current quality mode
 * @returns {string}
 */
export function getQualityMode() {
  return _qualityMode;
}

// ══════════════════════════════════════════════════════════════
//  10. PERFORMANCE SCORING
// ══════════════════════════════════════════════════════════════

/**
 * Run performance score (0-100)
 * Tests: FPS (3s), memory, GPU tier, network
 * @returns {Promise<{score:number, breakdown:Object}>}
 */
export async function getPerformanceScore() {
  try {
    const breakdown = { fps: 0, memory: 0, gpu: 0, network: 0 };
    
    // [IMPROVED] FPS test (3 секунды для точности)
    let frameCount = 0;
    const start = performance.now();
    await new Promise(resolve => {
      const tick = () => {
        frameCount++;
        if (performance.now() - start < 3000) {
          requestAnimationFrame(tick);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(tick);
    });
    
    const fps = frameCount / 3; // FPS за 3 секунды
    breakdown.fps = Math.min(30, fps) / 30 * 25; // max 25 points for 30+ FPS
    
    // Memory
    if (performance.memory) {
      const ratio = performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;
      breakdown.memory = (1 - ratio) * 25; // max 25 points
    } else {
      breakdown.memory = 15; // unknown = average
    }
    
    // GPU tier
    const gpuInfo = window.MikhmelViewer?.getGPUInfo?.();
    const tierScores = { High: 25, Medium: 15, Low: 5 };
    breakdown.gpu = tierScores[gpuInfo?.tier?.label] || 10;
    
    // Network
    const conn = navigator.connection;
    if (conn) {
      const speeds = { '4g': 25, '3g': 15, '2g': 5 };
      breakdown.network = speeds[conn.effectiveType] || 10;
    } else {
      breakdown.network = 20;
    }
    
    const score = Math.round(Object.values(breakdown).reduce((a, b) => a + b, 0));
    return { score, breakdown };
  } catch (e) {
    log.warn('[Mobile] getPerformanceScore failed:', e.message);
    return { score: 0, breakdown: {} };
  }
}

// ══════════════════════════════════════════════════════════════
//  11. STATE & CONTROL
// ══════════════════════════════════════════════════════════════

/**
 * Get full stability state
 * @returns {Object}
 */
export function getMobileStabilityState() {
  return {
    isTouch: IS_TOUCH,
    tutorialShown: _tutorialShown,
    qualityMode: _qualityMode,
    hasBatteryWatcher: !!_batteryWatcher,
    hasNetworkWatcher: !!_networkWatcher,
    touchEnforced: !!document.getElementById('mikhmel-touch-enforce'),
    orientation: screen.orientation?.type || 'unknown',
    viewport: `${window.innerWidth}×${window.innerHeight}`,
  };
}

export function showTutorial() {
  _tutorialShown = false;
  _showGestureTutorial();
}

export function resetTutorial() {
  try {
    localStorage.removeItem('mikhmel_gesture_tutorial_v2');
  } catch {
    // localStorage may be unavailable
  }
  _tutorialShown = false;
}

export function isTouchDevice() {
  return IS_TOUCH;
}

// ══════════════════════════════════════════════════════════════
//  INIT & DESTROY
// ══════════════════════════════════════════════════════════════

export function initMobileStability() {
  // [FIX] Cleanup previous listeners
  if (_ac) {
    _ac.abort();
  }
  _ac = new AbortController();
  
  if (!IS_TOUCH) {
    log.debug('[Mobile] Desktop detected — skipping mobile stability');
    return;
  }

  log.debug('[Mobile] Initializing mobile stability (Touch Enabled)...');

  enforceTouchAction();
  _preventNativeGestures();
  _handleOrientationChange();
  _applySafeAreaInsets();
  _setupVisibilityRecovery();

  // [10.0.1] Таймер туториала берётся под учёт и гасится в destroy.
  // Раньше он был «выстрелил и забыл»: после destroyMobileStability() диалог
  // всё равно всплывал через полторы секунды — уже поверх разобранного UI.
  clearTimeout(_tutorialTimer);
  _tutorialTimer = setTimeout(() => { _tutorialTimer = null; _showGestureTutorial(); }, 1500);
}

/**
 * [FIX] Полная очистка всех ресурсов
 */
export function destroyMobileStability() {
  // Abort all event listeners
  if (_ac) {
    _ac.abort();
    _ac = null;
  }
  
  // Clear throttles
  if (_visibilityThrottle) {
    clearTimeout(_visibilityThrottle);
    _visibilityThrottle = null;
  }

  // [10.0.1] ResizeObserver не подчиняется AbortController — гасим руками,
  // вместе с его отложенным debounce-таймером
  if (_resizeObserver) {
    try { _resizeObserver.disconnect(); } catch { /* уже отключён */ }
    _resizeObserver = null;
  }
  if (_resizeTimeout) {
    clearTimeout(_resizeTimeout);
    _resizeTimeout = null;
  }
  if (_tutorialTimer) {
    clearTimeout(_tutorialTimer);
    _tutorialTimer = null;
  }

  // Clear watchers
  _batteryWatcher = null;
  _networkWatcher = null;
  
  // Reset state
  _qualityMode = 'auto';
  _tutorialShown = false;
  
  log.debug('[Mobile] Mobile stability destroyed');
}

// Backward compatibility
export const IS_MOBILE = IS_TOUCH;